import axios from 'axios';
import { PatientEncounter } from '@finpay-development/shared-platform/lib/esm/patient/components/models/patient-encounter';
import { PatientViewModel } from '@finpay-development/shared-platform/lib/esm/patient/components/models/patient-view-model';
import { Rule } from '@finpay-development/shared-platform/lib/esm/admin-configuration/models/rule';
import { admissionsAdvisorService } from '@finpay-development/shared-platform/lib/esm/admissions-advisor/services/admissions-advisor-services';
import {
  axiosReadHelper,
} from '@finpay-development/shared-platform/lib/esm/shared/service/axios-read-helper';
import { AxiosReadPayload } from '@finpay-development/shared-platform/lib/esm/shared/service/axios-read-payload';
import { AxiosResultStatus } from '@finpay-development/shared-platform/lib/esm/shared/service/axios-result-status';
import {
  mapEncounterToRvkOutboundPayload,
  PayersMapLike,
  RvkVobSource,
} from './map-encounter-to-rvk-payload';
import { RvkOutboundCallPayload } from './rvk-outbound-call-payload';

/** Prototype: hardcoded RVK outbound endpoint + bearer token. */
const RVK_OUTBOUND_URL = 'https://pcr.rvkai.com/client/outbound/finpay';
const RVK_OUTBOUND_TOKEN = 'finpay_<secret>';

export type CallPatientResult =
  | { ok: true; callId?: string | number }
  | { ok: false; errorMessage: string };

async function fetchMatchingRule(encounter: PatientEncounter): Promise<Rule | null> {
  const timingRiskId = encounter.timingRisk?.timingRiskId;
  const payorRiskId = encounter.payorRisk?.payorRiskId;
  const pfrAmt = encounter.adjustedPfrAmt ?? encounter.pfrAmt;
  const optionalParams = [
    typeof timingRiskId === 'number' && `timingRiskId=${timingRiskId}`,
    typeof payorRiskId === 'number' && `payorRiskId=${payorRiskId}`,
    typeof pfrAmt === 'number' && `pfrAmt=${pfrAmt}`,
  ]
    .filter(Boolean)
    .join('&');
  const query = optionalParams
    ? `clientId=${encounter.clientId}&facilityId=${encounter.facilityId}&${optionalParams}`
    : `clientId=${encounter.clientId}&facilityId=${encounter.facilityId}`;
  const payload: AxiosReadPayload = {
    dataId: 0,
    url: `payment/v2/client/program/rule?${query}`,
  };
  const matchingRuleRes: AxiosResultStatus = await axiosReadHelper(payload);
  if (matchingRuleRes.hasErrors || matchingRuleRes.errorMessage || !matchingRuleRes.entity) {
    return null;
  }
  const ruleData = Array.isArray(matchingRuleRes.entity)
    ? matchingRuleRes.entity.length > 0
      ? matchingRuleRes.entity[0]
      : null
    : matchingRuleRes.entity;
  if (ruleData && ruleData.ruleId !== undefined && ruleData.ruleId !== null) {
    return ruleData as Rule;
  }
  return null;
}

async function fetchVobForEncounter(encounter: PatientEncounter): Promise<RvkVobSource | null> {
  const pfrEstimateId = encounter.pfrEstimateId;
  if (!pfrEstimateId || pfrEstimateId <= 0) {
    return null;
  }
  const estimateResponse = await admissionsAdvisorService.callV3EstimateGet(-2, pfrEstimateId);
  if (estimateResponse.hasErrors || !estimateResponse.entity) {
    return null;
  }
  const vobId = estimateResponse.entity?.vobId;
  if (!vobId) {
    return null;
  }
  const vobResponse = await admissionsAdvisorService.getVobV3(Number(vobId));
  if (vobResponse.hasErrors || !vobResponse.entity) {
    return null;
  }
  return vobResponse.entity as RvkVobSource;
}

export async function buildRvkOutboundPayload(
  encounter: PatientEncounter,
  selectedPatient: PatientViewModel | null | undefined,
  payersMap: PayersMapLike
): Promise<RvkOutboundCallPayload> {
  const [vob, matchingRule] = await Promise.all([
    fetchVobForEncounter(encounter),
    fetchMatchingRule(encounter),
  ]);
  return mapEncounterToRvkOutboundPayload({
    encounter,
    selectedPatient,
    vob,
    payersMap,
    matchingRule,
  });
}

export async function postRvkOutboundCall(
  payload: RvkOutboundCallPayload
): Promise<CallPatientResult> {
  try {
    const response = await axios.post(RVK_OUTBOUND_URL, payload, {
      headers: {
        Authorization: `Bearer ${RVK_OUTBOUND_TOKEN}`,
        'Content-Type': 'application/json',
      },
      validateStatus: (status) => status === 202 || (status >= 200 && status < 300),
    });
    if (response.status === 202 || (response.status >= 200 && response.status < 300)) {
      return { ok: true, callId: response.data?.id };
    }
    return {
      ok: false,
      errorMessage: `Call failed (${response.status})`,
    };
  } catch (err: any) {
    const message =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      err?.message ||
      'Call Patient request failed';
    return { ok: false, errorMessage: String(message) };
  }
}

export async function triggerCallPatient(
  encounter: PatientEncounter,
  selectedPatient: PatientViewModel | null | undefined,
  payersMap: PayersMapLike
): Promise<CallPatientResult> {
  const payload = await buildRvkOutboundPayload(encounter, selectedPatient, payersMap);
  return postRvkOutboundCall(payload);
}
